import { useState, useEffect } from "react";
import { useAuthContext } from "../../contexts/AuthContext";
import { getCourseCertificates, getCertificateStats } from "../../services/certificateService";
import { getDocs, collection, query, where } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { Certificate } from "../../types/certificate";
import { Award, GraduationCap, Calendar, BookOpen, Medal } from "lucide-react";

export default function CertificateManagement() {
  const { user } = useAuthContext();
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState<string>("all");
  const [courses, setCourses] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;

      try {
        // Fetch instructor's courses
        const coursesQuery = query(
          collection(db, "courses"),
          where("instructorId", "==", user.uid)
        );
        const coursesSnap = await getDocs(coursesQuery);
        const coursesData = coursesSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setCourses(coursesData);

        // Fetch all certificates for instructor's courses
        const allCertificates: Certificate[] = [];
        for (const course of coursesData) {
          const courseCerts = await getCourseCertificates(course.id);
          allCertificates.push(...courseCerts);
        }
        setCertificates(allCertificates);

        // Fetch stats
        const statsData = await getCertificateStats(user.uid);
        setStats(statsData);
      } catch (err) {
        console.error("Error fetching certificates:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const filteredCertificates =
    selectedCourse === "all"
      ? certificates
      : certificates.filter((cert) => cert.courseId === selectedCourse);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-textLight">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-20">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-textDark mb-2 flex items-center gap-3">
          <Award className="w-8 h-8 text-blue-600" />
          จัดการใบเซอร์ติฟิเคต
        </h1>
        <p className="text-textLight">
          ดูและจัดการใบเซอร์ติฟิเคตที่ออกให้นักเรียน
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gradient-to-br from-blue-500 to-pink-600 rounded-2xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <GraduationCap className="w-6 h-6" />
              </div>
            </div>
            <p className="text-sm opacity-90 mb-2">ใบเซอร์ติฟิเคตทั้งหมด</p>
            <p className="text-4xl font-bold">{stats.totalIssued}</p>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <Calendar className="w-6 h-6" />
              </div>
            </div>
            <p className="text-sm opacity-90 mb-2">ออกในเดือนนี้</p>
            <p className="text-4xl font-bold">{stats.issuedThisMonth}</p>
          </div>

          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <BookOpen className="w-6 h-6" />
              </div>
            </div>
            <p className="text-sm opacity-90 mb-2">คอร์สที่มีใบเซอร์</p>
            <p className="text-4xl font-bold">{stats.topCourses.length}</p>
          </div>
        </div>
      )}

      {/* Top Courses */}
      {stats && stats.topCourses.length > 0 && (
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <h2 className="text-xl font-bold text-textDark mb-4 flex items-center gap-2">
            <Medal className="w-6 h-6 text-yellow-500" />
            คอร์สยอดนิยม (ออกใบเซอร์มากที่สุด)
          </h2>
          <div className="space-y-3">
            {stats.topCourses.map((course: any, index: number) => (
              <div
                key={course.courseId}
                className="flex items-center justify-between p-4 bg-gradient-soft rounded-xl"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-gradient-primary rounded-full flex items-center justify-center text-white font-bold">
                    {index + 1}
                  </div>
                  <div>
                    <p className="font-semibold text-textDark">{course.courseTitle}</p>
                    <p className="text-sm text-textLight">
                      {course.count} ใบเซอร์ติฟิเคต
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
        <div className="flex items-center gap-4">
          <label className="font-semibold text-textDark">กรองตามคอร์ส:</label>
          <select
            value={selectedCourse}
            onChange={(e) => setSelectedCourse(e.target.value)}
            className="flex-1 px-4 py-2 rounded-xl border-2 border-gray-200 focus:border-primary focus:outline-none transition-colors"
          >
            <option value="all">ทุกคอร์ส ({certificates.length})</option>
            {courses.map((course) => (
              <option key={course.id} value={course.id}>
                {course.title} (
                {certificates.filter((cert) => cert.courseId === course.id).length})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Certificates List */}
      {filteredCertificates.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
          <GraduationCap className="w-24 h-24 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-textDark mb-2">
            ยังไม่มีใบเซอร์ติฟิเคต
          </h2>
          <p className="text-textLight">
            เมื่อนักเรียนเรียนจบคอร์สจะได้รับใบเซอร์ติฟิเคตอัตโนมัติ
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-purple-50 to-pink-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-textDark">
                    เลขที่ใบเซอร์
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-textDark">
                    ชื่อนักเรียน
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-textDark">
                    คอร์ส
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-textDark">
                    เกรด
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-textDark">
                    วันที่ออก
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-textDark">
                    การกระทำ
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredCertificates.map((cert) => (
                  <tr key={cert.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-mono text-sm text-textDark">
                        {cert.certificateNumber}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-semibold text-textDark">{cert.userName}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-textDark max-w-xs truncate">
                        {cert.courseTitle}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {cert.grade ? (
                        <span className="font-bold text-green-500">{cert.grade}</span>
                      ) : (
                        <span className="text-textLight">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-textDark">
                        {new Date(cert.issueDate).toLocaleDateString("th-TH")}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <a
                        href={`/verify/${cert.verificationCode}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:text-primaryDark font-semibold text-sm"
                      >
                        ดูใบเซอร์
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
